import {
  put,
  call,
  select,
  takeEvery,
  takeLatest,
  take,
  getContext,
  delay,
} from 'redux-saga/effects';
import { message } from 'antd';
import { selectCurrentDashboardUser } from '@redux/login/reducer';
import i18next from '../../i18n';
import {
  appId,
  videoCreationEndpoint,
  videoCEDetails,
  videoUbiquityExportDetails,
  videoExportStatusEndpoint,
} from 'settings';
import { HOSPITAL } from '@constants';
import createUbiquityClient from '@api/ubiquityClient';
import {
  FETCH_DOCUMENTS,
  LOAD_DOCUMENTS_FAILED,
  FETCH_DOCUMENT_VERSIONS,
  DOCUMENT_VERSIONS_LOADED,
  FETCH_DOCUMENT_PAGES,
  PUBLISH_DOCUMENTS,
  UNPUBLISH_DOCUMENTS,
  IRawDocument,
  IDocument,
} from './types';
import {
  fetchDocumentPublishedStatus,
  loadDocuments,
  createDocumentFailed,
  createDocumentSuccess,
  deleteDocumentsFailed,
  deleteDocumentsSuccess,
  documentsLoaded,
  fetchDocumentVersions,
  documentVersionsLoaded,
  documentPagesLoaded,
  IFetchDocumentPages,
  IFetchDocumentVersions,
  IDeleteDocuments,
  ILoadDocumentsFailed,
  ICreateDocument,
  IEditDocument,
  fetchDocumentPages,
  editDocumentSuccess,
  editDocumentFailed,
  loadDocumentsFailed,
  IDeleteDocument,
  changePublishedStatus,
  IPublishDocuments,
  IUnpublishDocuments,
  IFetchDocumentPublishedStatus,
  fetchDocumentPublishedStatusSuccess,
  publishDocumentsSuccess,
  publishDocumentsFailed,
  unpublishDocumentsSuccess,
  unpublishDocumentsFailed,
  ICreateVideo,
  createVideoSuccess,
  IEditVideo,
  editVideoSuccess,
  createVideoFailed,
  editVideoFailed,
  IGetDocumentPreview,
  documentPreviewFailed,
  documentPreviewLoaded,
} from './actions';
import { selectDocumentVersions, selectDocumentPublishedStatus } from './reducers';
import doCreateUbiquityClient from '../doCreateUbiquityClient';
import { doDetermineOwnerForContent } from '../doDetermineOwnerIdForContent';
import takeFirst from '../takeFirst';
import { VIDEO } from '@utils/contentTypes';

export default function* root() {
  yield takeFirst(FETCH_DOCUMENTS, doFetchDocuments);
  yield takeLatest(LOAD_DOCUMENTS_FAILED, doLoadFailed);
  yield takeLatest(FETCH_DOCUMENT_VERSIONS, doFetchDocumentVersions);
  yield takeEvery(FETCH_DOCUMENT_PAGES, doFetchDocumentPages);
  yield takeEvery(PUBLISH_DOCUMENTS, doPublishDocuments);
  yield takeEvery(UNPUBLISH_DOCUMENTS, doUnpublishDocuments);
  yield takeEvery('documents/deleteOne', doDeleteDocument);
  yield takeEvery('documents/delete', doDeleteDocuments);
  yield takeEvery('documents/create', doCreateDocument);
  yield takeEvery('documents/edit', doEditDocument);
  yield takeEvery('documents/fetchPublishedStatus', doFetchPublishedStatus);
  yield takeEvery('videos/create', doCreateVideo);
  yield takeEvery('videos/edit', doEditVideo);
  yield takeEvery('documents/get-preview', doGetDocumentPreview);
}

function createTagsFromMetadata(metadata: {
  language: string;
  source: string;
  hospitalId?: string;
}) {
  return [
    { term: metadata.language.toUpperCase(), label: metadata.language.toUpperCase() },
    { term: metadata.source.toUpperCase(), label: metadata.source.toUpperCase() },
    ...(metadata.hospitalId ? [{ term: metadata.hospitalId, label: 'HOSPITAL_ID' }] : []),
  ];
}

function findLatestDocumentVersion<T extends { version_number: number }>(
  input: T[],
): T | undefined {
  return input.reduce((acc: T | undefined, next) => {
    return (acc?.version_number || 0) > next.version_number ? acc : next;
  }, undefined);
}

function* doDeleteDocuments({
  payload: { documentIds, documentTranslationKey },
}: IDeleteDocuments) {
  try {
    const client = yield call(doCreateUbiquityClient);

    for (const documentId of documentIds) {
      yield call(client.deleteDocument, appId, documentId);
    }

    yield put(deleteDocumentsSuccess(documentIds.map(documentId => `${documentId}`)));
    yield call(
      message.success,
      i18next.t(`${documentTranslationKey}:DocumentDetail.deleteSuccess`),
    );
  } catch (err) {
    console.error(err);
    yield put(deleteDocumentsFailed());
    yield call(message.error, i18next.t(`${documentTranslationKey}:DocumentDetail.deleteFailed`));
  }
}

function* doDeleteDocument({ payload: { documentId, documentTranslationKey } }: IDeleteDocument) {
  try {
    const client = yield call(doCreateUbiquityClient);
    const history = yield getContext('history');

    yield call(client.deleteDocument, appId, documentId);

    yield put(deleteDocumentsSuccess([documentId]));
    yield call(history.goBack);
    yield call(
      message.success,
      i18next.t(`${documentTranslationKey}:DocumentDetail.deleteSuccess`),
    );
  } catch (err) {
    console.error(err);
    yield put(deleteDocumentsFailed());
    yield call(message.error, i18next.t(`${documentTranslationKey}:DocumentDetail.deleteFailed`));
  }
}

function* doFetchDocuments() {
  try {
    yield put(loadDocuments());
    const client = yield call(doCreateUbiquityClient);
    const ownerId = yield call(doDetermineOwnerForContent);
    const rawDocuments: IRawDocument[] = yield call(client.documents, appId, {
      owner_id: ownerId,
    });
    const documents = rawDocuments
      .map(d => ({
        ...d,
        id: d.id,
        name: d.name,
        description: d?.latest_version?.description || d?.metadata?.custom?.description || '',
        latest_version: {
          ...d.latest_version,
          name: d.latest_version.name.replace('Version ', ''),
        },
        published: true,
      }))
      .sort((a, b) => (a.id > b.id ? -1 : 1));

    yield put(documentsLoaded(documents));
  } catch (err) {
    console.error(err);
    yield put(loadDocumentsFailed('documents'));
  }
}

function* doLoadFailed({ payload: { documentTranslationKey } }: ILoadDocumentsFailed) {
  yield call(message.error, i18next.t(`${documentTranslationKey}:failedLoad`));
}

function* doFetchDocumentVersions({ payload: { documentId, force } }: IFetchDocumentVersions) {
  const existing = yield select(state => state.documents.versions[documentId]);
  if (existing && !force) {
    yield put(documentVersionsLoaded(documentId, existing));
    return;
  }

  const tokens = yield getContext('tokens');
  const client = yield call(createUbiquityClient, tokens);
  const versions = yield call(client.documentVersions, appId, documentId);
  yield put(documentVersionsLoaded(documentId, versions));
}

function* doFetchDocumentPages({ payload: { documentId, force } }: IFetchDocumentPages) {
  const existing = yield select(state => state.documents.pages[documentId]);
  if (existing && !force) {
    yield put(documentPagesLoaded(documentId, existing));
    return;
  }
  yield put(fetchDocumentVersions(documentId, force));

  while (true) {
    const { payload } = yield take(DOCUMENT_VERSIONS_LOADED);
    if (payload.documentId === documentId) {
      break;
    }
  }

  const version = yield select(state => state.documents.versions[documentId][0]);
  const tokens = yield getContext('tokens');
  const client = yield call(createUbiquityClient, tokens);
  const pages = yield call(client.pages, version);

  yield put(documentPagesLoaded(documentId, pages));
}

function* doCreateDocument({ payload: { newDocument } }: ICreateDocument) {
  try {
    const history = yield getContext('history');
    const client = yield call(doCreateUbiquityClient);
    const ownerId = yield call(doDetermineOwnerForContent);
    const createdDocument = yield call(
      client.createDocument,
      appId,
      newDocument.name,
      null,
      ownerId,
    );
    const {
      id: documentId,
      latest_version: { id: versionId, name: versionName },
    } = createdDocument;

    yield call(client.editDocumentVersion, appId, documentId, versionId, {
      description: newDocument.description,
    });

    if (newDocument.metadata.source === HOSPITAL) {
      const currentDashboardUser = yield select(selectCurrentDashboardUser);
      newDocument.metadata.hospitalId = currentDashboardUser.profile.hospitalId;
    }

    const tags = createTagsFromMetadata(newDocument.metadata);
    yield call(client.editDocument, appId, documentId, newDocument.name, { tags });

    yield call(
      doUploadDocumentVersion,
      documentId,
      versionId,
      versionName,
      newDocument.fileData.originFileObj,
    );

    yield call(client.publish, appId, documentId, versionId);
    if (newDocument.published) {
      yield call(doPublishDocumentVersion, documentId, versionId, newDocument.published);
    }

    const finalDocument: IDocument = yield call(client.document, appId, documentId);

    yield put(
      createDocumentSuccess({
        ...finalDocument,
        description: finalDocument?.latest_version?.description || '',
      }),
    );
    yield call(history.push, '/content/documents');
    yield call(message.success, i18next.t('documents:Wizard.review.success'));
  } catch (err) {
    console.error(err);
    yield call(message.error, i18next.t('documents:Wizard.review.error'));
    yield put(createDocumentFailed());
  }
}

function* doEditDocument({ payload: { documentId, editedDocument } }: IEditDocument): any {
  try {
    const history = yield getContext('history');
    const client = yield call(doCreateUbiquityClient);
    const latestVersion = yield call(doGetLatestDocumentVersion, documentId);

    if (editedDocument.metadata.source === HOSPITAL) {
      const currentDashboardUser = yield select(selectCurrentDashboardUser);
      editedDocument.metadata.hospitalId = currentDashboardUser.profile.hospitalId;
    }

    const tags = createTagsFromMetadata(editedDocument.metadata);
    yield call(client.editDocument, appId, documentId, editedDocument.name, { tags });

    const documentVersion = yield call(client.createDocumentVersion, appId, documentId, {
      name: editedDocument.name,
      description: editedDocument.description,
      ...(editedDocument.fileData.originFileObj
        ? { creationMode: 'empty' }
        : { creationMode: 'copy', createFromVersion: latestVersion.id }),
    });

    const { id: versionId, name: versionName } = documentVersion;

    yield call(client.editDocumentVersion, appId, documentId, versionId, {
      description: editedDocument.description,
    });

    if (editedDocument.fileData.originFileObj) {
      yield call(
        doUploadDocumentVersion,
        documentId,
        versionId,
        versionName,
        editedDocument.fileData.originFileObj,
      );
      yield call(client.publish, appId, documentId, versionId);
    }

    yield call(doPublishDocumentVersion, documentId, versionId, editedDocument.published);

    const updatedDocument: IDocument = yield call(client.document, appId, documentId);
    yield put(fetchDocumentVersions(`${documentId}`));
    const pages = yield call(client.pages, documentVersion);
    yield put(documentPagesLoaded(`${documentId}`, pages));
    yield put(fetchDocumentPublishedStatus(documentId, true));
    yield put(
      editDocumentSuccess(documentId, {
        ...updatedDocument,
        description: updatedDocument?.latest_version?.description || '',
        latest_version: {
          ...updatedDocument?.latest_version,
          name: updatedDocument?.latest_version.name.replace('Version ', ''),
        },
      }),
    );
    yield call(history.goBack);
    yield call(message.success, i18next.t('documents:Wizard.review.success'));
  } catch (err) {
    console.error(err);
    yield call(message.error, i18next.t('documents:Wizard.review.error'));
    yield put(editDocumentFailed());
  }
}

function* doPublishDocumentVersion(documentId: number, versionId: number, publish: boolean) {
  const client = yield call(doCreateUbiquityClient);

  // ubiquity availability == published status in dashboard
  yield call(client.setDocumentAvailability, appId, documentId, publish ? 'free' : 'unavailable');
}

function* doUploadDocumentVersion(
  documentId: number,
  versionId: number,
  versionName: string,
  file: File,
) {
  const client = yield call(doCreateUbiquityClient);

  yield call(
    client.uploadDocumentVersion,
    appId,
    documentId,
    versionId,
    {
      fileName: file.name,
      issueName: versionName,
      // crocodoc == pdf for historical ubiquity reasons
      importType: 'crocodoc',
    },
    file,
  );
}

function* doPublishDocuments({ payload: { documentIds } }: IPublishDocuments) {
  try {
    for (const documentId of documentIds) {
      const latestVersion = yield call(doGetLatestDocumentVersion, documentId);
      yield call(doPublishDocumentVersion, documentId, latestVersion.id, true);
      yield put(changePublishedStatus(documentId, true));
    }

    yield put(publishDocumentsSuccess(documentIds));
    yield call(message.success, i18next.t('documents:DocumentDetail.publishSuccess'));
  } catch (err) {
    console.error(err);
    yield put(publishDocumentsFailed());
    yield call(message.error, i18next.t('documents:DocumentDetail.publishFailed'));
  }
}

function* doUnpublishDocuments({ payload: { documentIds } }: IUnpublishDocuments) {
  try {
    for (const documentId of documentIds) {
      const latestVersion = yield call(doGetLatestDocumentVersion, documentId);

      yield call(doPublishDocumentVersion, documentId, latestVersion.id, false);
      yield put(changePublishedStatus(documentId, false));
    }

    yield put(unpublishDocumentsSuccess(documentIds));
    yield call(message.success, i18next.t('documents:DocumentDetail.unpublishSuccess'));
  } catch (err) {
    console.error(err);
    yield put(unpublishDocumentsFailed());
    yield call(message.error, i18next.t('documents:DocumentDetail.unpublishFailed'));
  }
}

function* doGetLatestDocumentVersion(documentId: string | number) {
  const versionData = yield select(selectDocumentVersions(`${documentId}`));
  const latestVersion = yield call(findLatestDocumentVersion, versionData);

  return latestVersion;
}

function* doFetchPublishedStatus({
  payload: { documentId, force },
}: IFetchDocumentPublishedStatus) {
  try {
    const cachedStatus = yield select(selectDocumentPublishedStatus(`${documentId}`));

    if (cachedStatus !== undefined && !force) return;

    const client = yield call(doCreateUbiquityClient);
    const status = yield call(client.documentPublishedStatus, appId, documentId);

    yield put(fetchDocumentPublishedStatusSuccess(documentId, status.availability === 'free'));
  } catch (err) {
    console.error(err);
  }
}

function* callVideoCreationService(postData: object) {
  let exportDetails = undefined;
  // let ceAPIKey = undefined;
  try {
    const resp = yield fetch(videoCreationEndpoint, {
      method: 'POST',
      body: JSON.stringify(postData),
    });
    const data = yield call([resp, resp.json]);

    exportDetails = data['export_details'];
    // ceAPIKey = data['ce_api_key'];
  } catch (e) {
    console.error(e);
    return null;
  }
  let exportStatus = undefined;
  let detailsData = undefined;
  // const fd = new FormData();
  // fd.append('url', exportDetails['url']);
  while (exportStatus !== 'complete' && exportStatus !== 'failed') {
    yield delay(1000);
    const detailsResp = yield fetch(videoExportStatusEndpoint, {
      method: 'POST',
      body: JSON.stringify({ url: exportDetails['url'] }),
      headers: { 'Content-Type': 'application/json' },
    });
    detailsData = yield call([detailsResp, detailsResp.json]);
    exportStatus = detailsData['status'];
  }
  return detailsData;
}

function buildPostDataForVideo(
  videoDocument: any,
  existingCEDocumentUUID: any,
  existingUbiquityDocumentID: any,
  hospitalId?: string,
) {
  const originalData = videoDocument;
  const sourceTag = originalData.metadata.source.toUpperCase();
  const languageTag = originalData.metadata.language.toUpperCase();
  const publishDateLabel = i18next.t('videos:Wizard.details.publishDate');
  const runTimeLabel = i18next.t('videos:Wizard.details.runTime');
  const video_details: string[] = [];
  if (videoDocument.videoData.publishDate) {
    video_details.push(`${publishDateLabel}: ${videoDocument.videoData.publishDate}`);
  }
  if (videoDocument.videoData.runTime) {
    video_details.push(`${runTimeLabel}: ${videoDocument.videoData.runTime}`);
  }
  const customMetadataContent = {
    heading: originalData.videoData.videoTitle,
    video_id: originalData.videoData.videoId,
    description: originalData.videoData.videoDescription,
    publishDate: videoDocument.videoData.publishDate,
    runTime: videoDocument.videoData.runTime,
  };
  const pageFields = { ...customMetadataContent, video_details };
  const templateName = videoCEDetails.template_name.replace(
    'LANGUAGE',
    originalData.metadata.language,
  );
  const postData = {
    ...videoCEDetails,
    ce_doc_uuid: null,
    ce_doc_name: originalData.name,
    template_name: templateName,
    variables: {
      document: {
        custom_metadata: JSON.stringify({
          description: originalData.description,
          content: customMetadataContent,
        }),
        tags: `[{"term": "${sourceTag}","label": "${sourceTag}"},{"term": "${languageTag}","label": "${languageTag}"},{"term": "VIDEO","label": "VIDEO"} ${
          originalData.metadata.source === HOSPITAL
            ? `,{"term": "${hospitalId}", "label": "HOSPITAL_ID"}`
            : ''
        }]`,
      },
      pages: {
        'page-1': {
          title: 'Page 1',
          tags: '[]',
          fields: pageFields,
        },
      },
    },
    export_details: {
      ...videoUbiquityExportDetails,
      use_existing_document: false,
      ubiquity_existing_document_id: null,
    },
  };
  if (existingCEDocumentUUID) {
    postData.ce_doc_uuid = existingCEDocumentUUID;
    postData.export_details.use_existing_document = true;
    postData.export_details.ubiquity_existing_document_id = existingUbiquityDocumentID;
  }
  return postData;
}

function* doCreateVideo({ payload: { newDocument } }: ICreateVideo) {
  const history = yield getContext('history');
  try {
    const currentDashboardUser = yield select(selectCurrentDashboardUser);
    const ownerId = yield call(doDetermineOwnerForContent);
    const postData = buildPostDataForVideo(
      newDocument,
      null,
      null,
      currentDashboardUser.profile.hospitalId,
    );
    const exportDetails = yield call(callVideoCreationService, postData);
    if (exportDetails['status'] === 'complete') {
      const re = /documents\/(\d+)\/content/;
      const ubiquityDocumentId = parseInt(exportDetails['file'].match(re)[1]);
      const client = yield call(doCreateUbiquityClient);
      const finalDocument: IDocument = yield call(client.document, appId, ubiquityDocumentId);

      yield call(
        client.editDocument,
        appId,
        ubiquityDocumentId,
        newDocument.name,
        undefined,
        ownerId,
      );

      const versions = yield call(client.documentVersions, appId, ubiquityDocumentId);

      yield call(
        doPublishDocumentVersion,
        ubiquityDocumentId,
        versions[0].id,
        newDocument.published,
      );

      yield put(
        createVideoSuccess({
          ...finalDocument,
          description: finalDocument?.metadata?.custom?.description || '',
        }),
      );
      yield call(history.push, `/content/videos/${finalDocument.id}`);
      yield call(message.success, i18next.t('videos:Wizard.review.success'));
    } else {
      console.error('Video export from CE to Ubiquity returned status: ', exportDetails['status']);
      yield call(message.error, i18next.t('videos:Wizard.review.error'));
      yield put(createVideoFailed());
    }
  } catch (err) {
    console.error(err);
    yield call(message.error, i18next.t('videos:Wizard.review.error'));
    yield put(createVideoFailed());
  }
}

function* doEditVideo({
  payload: { ceDocumentUUID, ubiquityDocumentId, editedDocument },
}: IEditVideo): any {
  const history = yield getContext('history');
  try {
    const currentDashboardUser = yield select(selectCurrentDashboardUser);
    const postData = buildPostDataForVideo(
      editedDocument,
      ceDocumentUUID,
      ubiquityDocumentId,
      currentDashboardUser.profile.hospitalId,
    );
    const exportDetails = yield call(callVideoCreationService, postData);
    if (exportDetails['status'] === 'complete') {
      const client = yield call(doCreateUbiquityClient);

      const tags = createTagsFromMetadata(editedDocument.metadata);
      yield call(client.editDocument, appId, ubiquityDocumentId, editedDocument.name, {
        tags: [...tags, { term: VIDEO, label: VIDEO }],
      });

      const finalDocument: IDocument = yield call(client.document, appId, ubiquityDocumentId);

      const latestVersion = yield call(doGetLatestDocumentVersion, ubiquityDocumentId);
      yield call(
        doPublishDocumentVersion,
        ubiquityDocumentId,
        latestVersion.id,
        editedDocument.published,
      );
      yield put(fetchDocumentPublishedStatus(ubiquityDocumentId, true));

      yield put(
        editVideoSuccess(ubiquityDocumentId, {
          ...finalDocument,
          description: finalDocument?.metadata?.custom?.description || '',
        }),
      );

      yield put(fetchDocumentPages(ubiquityDocumentId.toString(), '', true));

      yield call(history.push, `/content/videos/${ubiquityDocumentId}`);
      yield call(message.success, i18next.t('videos:Wizard.review.success'));
    } else {
      console.error('Video export from CE to Ubiquity returned status: ', exportDetails['status']);
      yield call(message.error, i18next.t('videos:Wizard.review.error'));
      yield put(editVideoFailed());
    }
  } catch (err) {
    console.error(err);
    yield call(message.error, i18next.t('videos:Wizard.review.error'));
    yield put(editVideoFailed());
  }
}

function* doGetDocumentPreview({ payload: { documentId } }: IGetDocumentPreview) {
  try {
    const client = yield call(doCreateUbiquityClient);
    const versions = yield call(client.documentVersions, appId, documentId);
    const versionDetails = yield call(client.documentVersion, appId, documentId, versions[0].id);
    const pages = yield call(client.pages, versionDetails);

    yield put(
      documentPreviewLoaded(
        pages.map(
          ({ latest_published_url }: { latest_published_url: string }) => latest_published_url,
        ),
      ),
    );
  } catch (err) {
    console.error(err);
    yield put(documentPreviewFailed());
  }
}
